import React, { useState } from 'react';
import Icon from '../components/Icon';
import { gapLabel } from '../product/productUtils';
import { answerValues, isAnswered } from './onboardingUtils';

const OTHER_LABEL = { targetCustomer: 'Someone else…' };

const QuizStep = ({ draft, productName, isBusy, onBack, onDone, onProgress }) => {
    const { quiz, fields } = draft;
    const [answers, setAnswers] = useState(draft.answers || {});
    const [index, setIndex] = useState(Math.min(draft.qIndex || 0, quiz.length - 1));
    const question = quiz[index];
    const answer = answers[question.field] || { selected: [], other: '' };
    const [otherOpen, setOtherOpen] = useState(!!answer.other);
    const hasOptions = question.options?.length > 0;

    const setAnswer = (changes) => setAnswers(prev => ({
        ...prev,
        [question.field]: { selected: [], other: '', ...prev[question.field], ...changes, skipped: false },
    }));

    const toggleOption = (label) => {
        const selected = answer.selected || [];
        if (question.multi) {
            setAnswer({ selected: selected.includes(label) ? selected.filter(item => item !== label) : [...selected, label] });
        } else {
            setAnswer({ selected: selected.includes(label) ? [] : [label], other: '' });
            setOtherOpen(false);
        }
    };

    const move = (nextAnswers, nextIndex) => {
        if (nextIndex >= quiz.length) {
            onDone(nextAnswers);
            return;
        }
        if (nextIndex < 0) {
            onProgress(nextAnswers, 0);
            onBack();
            return;
        }
        onProgress(nextAnswers, nextIndex);
        setIndex(nextIndex);
        setOtherOpen(!!nextAnswers[quiz[nextIndex].field]?.other);
    };

    const skip = () => {
        const next = { ...answers, [question.field]: { selected: [], other: '', skipped: true } };
        setAnswers(next);
        move(next, index + 1);
    };

    const isLast = index === quiz.length - 1;
    const canContinue = isAnswered(answer);

    // Right panel: what the profile knows so far
    const summary = (field) => answerValues(answers[field]).join(', ');

    return (
        <div className="ls-flow-split">
            <div className="ls-flow-center" style={{ padding: '48px 40px' }}>
                <div className="ls-flow-column" style={{ width: 640 }}>
                    <div className="ls-quiz-progress" aria-hidden="true">
                        {quiz.map((q, i) => <span key={q.field} className={i < index ? 'is-done' : i === index ? 'is-current' : ''} />)}
                        <span className="ls-quiz-count">Question {index + 1} of {quiz.length}</span>
                    </div>
                    <div>
                        <h1 className="ls-flow-h1">{question.question}</h1>
                        {question.helper && <div className="ls-flow-lead">{question.helper}</div>}
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }} role={question.multi ? 'group' : 'radiogroup'} aria-label={question.question}>
                        {(question.options || []).map(option => {
                            const selected = (answer.selected || []).includes(option.label);
                            const icon = question.multi
                                ? (selected ? 'check_box' : 'check_box_outline_blank')
                                : (selected ? 'radio_button_checked' : 'radio_button_unchecked');
                            return (
                                <button
                                    type="button"
                                    key={option.label}
                                    role={question.multi ? 'checkbox' : 'radio'}
                                    aria-checked={selected}
                                    className={`ls-option${selected ? ' is-selected' : ''}`}
                                    onClick={() => toggleOption(option.label)}
                                >
                                    <Icon name={icon} size={22} filled={selected} />
                                    <div style={{ flex: 1 }}>
                                        <div className="ls-option-title">{option.label}</div>
                                        {option.evidence && <div className="ls-option-evidence">{option.evidence}</div>}
                                    </div>
                                </button>
                            );
                        })}

                        {hasOptions && !otherOpen ? (
                            <button type="button" className="ls-option is-other" onClick={() => setOtherOpen(true)}>
                                <Icon name="edit" size={22} />{OTHER_LABEL[question.field] || 'Something else…'}
                            </button>
                        ) : (
                            <textarea
                                className="ls-textarea"
                                rows={hasOptions ? 2 : 3}
                                autoFocus
                                value={answer.other || ''}
                                onChange={(e) => setAnswer(question.multi || !hasOptions ? { other: e.target.value } : { other: e.target.value, selected: [] })}
                                placeholder={hasOptions ? 'Describe it in your own words' : 'Your answer'}
                                aria-label={question.question}
                                style={{ borderRadius: 14 }}
                            />
                        )}
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, paddingTop: 4 }}>
                        <button type="button" className="ls-btn ls-btn-ghost" onClick={() => move(answers, index - 1)} disabled={isBusy}>Back</button>
                        <button type="button" className="ls-btn ls-btn-ghost" style={{ marginLeft: 'auto' }} onClick={skip} disabled={isBusy}>Skip</button>
                        <button type="button" className="ls-btn ls-btn-dark" onClick={() => move(answers, index + 1)} disabled={!canContinue || isBusy}>
                            {isBusy ? 'Saving…' : isLast ? 'Draft ICPs' : 'Next'}
                        </button>
                    </div>
                </div>
            </div>

            <aside className="ls-flow-aside is-right">
                <div style={{ fontSize: 12, fontWeight: 600, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--muted)' }}>
                    {productName} profile so far
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                    <div className="ls-profile-item">
                        <Icon name="check_circle" size={18} color="var(--accent-text)" />
                        <div>
                            <div style={{ fontWeight: 600 }}>Basics, pricing, features</div>
                            <div style={{ fontSize: 13, color: 'var(--muted)' }}>
                                {draft.sources.some(s => s.status === 'ready') ? 'From sources' : 'From your review'}
                            </div>
                        </div>
                    </div>
                    {quiz.map((q, i) => {
                        const done = isAnswered(answers[q.field]);
                        const skipped = answers[q.field]?.skipped;
                        const label = gapLabel(q.field, fields[q.field]?.value);
                        if (i === index) {
                            return (
                                <div key={q.field} className="ls-profile-item is-current">
                                    <Icon name="pending" size={18} color="var(--accent-strong)" />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600 }}>{label}</div>
                                        {done && <div style={{ fontSize: 13, color: 'var(--muted)' }}>{summary(q.field)}</div>}
                                    </div>
                                </div>
                            );
                        }
                        if (done) {
                            return (
                                <div key={q.field} className="ls-profile-item">
                                    <Icon name="check_circle" size={18} color="var(--accent-text)" />
                                    <div style={{ minWidth: 0 }}>
                                        <div style={{ fontWeight: 600 }}>{label}</div>
                                        <div style={{ fontSize: 13, color: 'var(--muted)' }}>{summary(q.field)}</div>
                                    </div>
                                </div>
                            );
                        }
                        return (
                            <div key={q.field} className="ls-profile-item is-todo">
                                <Icon name="radio_button_unchecked" size={18} color="var(--border-input)" />
                                {label}{skipped ? ' · skipped' : ''}
                            </div>
                        );
                    })}
                </div>
                <div style={{ marginTop: 'auto', background: 'var(--page)', borderRadius: 12, padding: 14, fontSize: 13, color: 'var(--secondary)', lineHeight: 1.5 }}>
                    Your answers here seed the ICPs on the next step.
                </div>
            </aside>
        </div>
    );
};

export default QuizStep;
